import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useGetPrompt } from '@/hooks/usePrompts';
import { useSession } from '@/auth/SessionProvider';
import { SpinnerFullPage } from '@/components/ui';
import { Banner, Divider } from '@telegram-apps/telegram-ui';
import { TelegramDualButtons } from '../../../../components/TelegramDualButtons';
import { Fragment } from 'react/jsx-runtime';
import { buildShareUrl } from '@/utils/shareUrl';
import { useSelectCommunity } from '@/contexts/SelectCommunityContext';
import { canShareMessage, shareTelegramMessage } from '@/utils/telegramShare';
import { IoShareOutline } from 'react-icons/io5';
import { ContentList } from './ContentList';

export const Route = createFileRoute('/content/$promptId/details/')({
  component: ContentDetails,
});

// Helper function to get display label for prompt type
const getTypeLabel = (
  type: 'images' | 'stickers' | 'gifs' | 'animated_stickers'
): string => {
  switch (type) {
    case 'images':
      return 'Image';
    case 'stickers':
      return 'Sticker';
    case 'gifs':
      return 'GIF';
    case 'animated_stickers':
      return 'Animated Sticker';
    default:
      return type;
  }
};

function ContentDetails() {
  const { promptId } = Route.useParams();
  const navigate = useNavigate();
  const { session, isAuthenticating } = useSession();
  const { selectedCommunity } = useSelectCommunity();

  const { data: prompt, isLoading, error } = useGetPrompt(promptId, session);

  console.log('PROMPT DETAILS DATA:', prompt);

  const handleContinue = () => {
    navigate({
      to: '/content/$promptId/select-nfts',
      params: { promptId },
    });
  };

  const handleShare = () => {
    const shareUrl = buildShareUrl(
      import.meta.env.VITE_PUBLIC_BOT_URL || '',
      `/content/${promptId}/details`,
      selectedCommunity?.id
    );

    shareTelegramMessage(shareUrl, `Check out this prompt: ${prompt?.name}`);
  };

  if (isAuthenticating) {
    return <SpinnerFullPage text="Authenticating..." />;
  }
  if (isLoading) {
    return <SpinnerFullPage text="Loading template details..." />;
  }

  if (error || !prompt) {
    return (
      <div className="mx-auto max-w-4xl p-4">
        <div className="flex items-center justify-center p-8">
          <div className="text-center">
            <div className="mb-4 text-4xl">⚠️</div>
            <h2 className="text-tg-text mb-2 text-xl font-bold">
              Failed to load prompt
            </h2>
            <p className="text-tg-hint mb-4">
              {error?.message || 'Prompt not found'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // TypeScript now knows prompt is defined here
  // Get all preview images
  const getPreviewImages = () => {
    if (prompt.stickers && prompt.stickers.length > 0) return prompt.stickers;
    if (prompt.gifs && prompt.gifs.length > 0) return prompt.gifs;
    if (prompt.images && prompt.images.length > 0) return prompt.images;
    if (prompt.videos && prompt.videos.length > 0) return prompt.videos;
    if (prompt.animatedStickers && prompt.animatedStickers.length > 0)
      return prompt.animatedStickers;
    return [];
  };

  const previewImages = getPreviewImages();

  return (
    <div className="mx-auto max-w-4xl p-2">
      <div className="">
        {/* Pack Header */}
        <div className="rounded-lg">
          <div className="relative rounded-lg p-2">
            <div className="flex w-full flex-col items-center">
              {/* Collection Info */}
              <div className="w-full">
                <div className="flex items-center justify-center gap-3">
                  <h1 className="text-2xl font-bold">
                    {prompt.name || 'Untitled Prompt'}
                  </h1>
                  {canShareMessage() && (
                    <button
                      onClick={handleShare}
                      className="text-tg-hint hover:text-tg-accent-text transition-colors active:scale-95"
                      aria-label="Share prompt"
                    >
                      <IoShareOutline className="h-5 w-5" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Description */}
        {prompt.description && (
          <div className="p-6">
            <p className="text-tg-hint text-sm">{prompt.description}</p>
          </div>
        )}

        <Divider />

        {/* Content List */}
        {previewImages.length > 0 && (
          <div>
            <Banner
              header={
                <h2 className="text-tg-text text-lg">
                  Generations from other users
                </h2>
              }
              description={
                <div>
                  <p className="text-xs">
                    Example of{' '}
                    {getTypeLabel(
                      prompt.type as
                        | 'images'
                        | 'stickers'
                        | 'gifs'
                        | 'animated_stickers'
                    ).toLowerCase()}{' '}
                    generated by other users.
                  </p>
                  <p className="text-tg-accent-text text-xs">
                    You will receive 1 personalized{' '}
                    {getTypeLabel(
                      prompt.type as
                        | 'images'
                        | 'stickers'
                        | 'gifs'
                        | 'animated_stickers'
                    ).toLowerCase()}
                  </p>
                </div>
              }
              style={{
                backgroundColor: 'var(--tg-secondary-bg)',
              }}
            >
              <Fragment>
                <ContentList prompt={prompt} />
              </Fragment>
            </Banner>
          </div>
        )}

        {/* Get Started Button */}
        <TelegramDualButtons
          mainButton={{
            text: `Get Yours`,
            onClick: handleContinue,
            visible: true,
          }}
        />
      </div>
    </div>
  );
}
